"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Configuration = void 0;
const echain_shared_articles_oo_1 = require("@igus/echain-shared-articles-oo");
const echain_shared_articles_1 = require("@igus/echain-shared-articles");
const echain_shared_various_1 = require("@igus/echain-shared-various");
const echain_shared_i18n_1 = require("@igus/echain-shared-i18n");
const echain_shared_logging_1 = require("@igus/echain-shared-logging");
const echain_shared_quantities_1 = require("@igus/echain-shared-quantities");
const echain_shared_ts_utils_1 = require("@igus/echain-shared-ts-utils");
const echain_shared_validation_1 = require("@igus/echain-shared-validation");
const echain_shared_igus_fundamentals_1 = require("@igus/echain-shared-igus-fundamentals");
const version_1 = require("./version");
const echain_shared_configurations_1 = require("@igus/echain-shared-configurations");
const VerpackungCalculator_1 = require("./VerpackungCalculator");
/**
 * The `Configuration` represents a configured set of echain articles, building a functional
 * application.
 * It keeps all article members protected to disallow unmanaged changes.
 * Classes extending this shall provide change actions as access layer for a configuration.
 */
class Configuration {
    get domainVersion() {
        return this.model.DomainVersion;
    }
    get managedConfigurationType() {
        return this.model.ManagedConfigurationType;
    }
    set managedConfigurationType(value) {
        this.model.ManagedConfigurationType = value;
    }
    get configurationStartedWith() {
        return this.model.ConfigurationStartedWith;
    }
    set configurationStartedWith(value) {
        this.model.ConfigurationStartedWith = value;
    }
    get _isAlienConfiguration() {
        return this.model.IsAlienConfiguration;
    }
    set _isAlienConfiguration(value) {
        this.model.IsAlienConfiguration = value;
    }
    get isAlienConfiguration() {
        return this.model.IsAlienConfiguration;
    }
    set isAlienConfiguration(value) {
        this.model.IsAlienConfiguration = value;
    }
    set _iaAuthorName(value) {
        this.model.IAAuthorName = value;
    }
    get _iaAuthorName() {
        return this.model.IAAuthorName;
    }
    set iaAuthorName(value) {
        this.model.IAAuthorName = value;
    }
    get iaAuthorName() {
        return this.model.IAAuthorName;
    }
    set _iaRemark(value) {
        this.model.IARemark = value;
    }
    get _iaRemark() {
        return this.model.IARemark;
    }
    set iaRemark(value) {
        this.model.IARemark = value;
    }
    get iaRemark() {
        return this.model.IARemark;
    }
    set _iaCompanyId(value) {
        this.model.IACompanyId = value;
    }
    get _iaCompanyId() {
        return this.model.IACompanyId;
    }
    set iaCompanyId(value) {
        this.model.IACompanyId = value;
    }
    get iaCompanyId() {
        return this.model.IACompanyId;
    }
    set _iaCompanyName(value) {
        this.model.IACompanyName = value;
    }
    get _iaCompanyName() {
        return this.model.IACompanyName;
    }
    set iaCompanyName(value) {
        this.model.IACompanyName = value;
    }
    get iaCompanyName() {
        return this.model.IACompanyName;
    }
    set igusGUIds(value) {
        this.model.IgusGUIds = value;
    }
    get igusGUIds() {
        return this.model.IgusGUIds;
    }
    getCurrentIgusGUId() {
        return this.igusGUIds[this.igusGUIds.length - 1];
    }
    /**
     * returns the current IgusGUId if it valid else undefined
     */
    get igusGUId() {
        var _a;
        return this.isIgusGUIdValid() ? (_a = this.getCurrentIgusGUId()) === null || _a === void 0 ? void 0 : _a.id : undefined;
    }
    /**
     * returns a clone of all IgusGUIds
     */
    getIgusGUIdsClone() {
        return (0, echain_shared_ts_utils_1.clonePrimitive)(this.igusGUIds);
    }
    /**
     * Return if the current IgusGUId marked as dirty or not
     */
    isIgusGUIdDirty() {
        var _a;
        return (_a = this.getCurrentIgusGUId()) === null || _a === void 0 ? void 0 : _a.dirty;
    }
    /**
     * Return if the current IgusGUId marked as exposed or not
     */
    isIgusGUIdExposed() {
        var _a;
        return this.igusGUIds.length ? (_a = this.getCurrentIgusGUId()) === null || _a === void 0 ? void 0 : _a.exposed : undefined;
    }
    /**
     * Return if the current IgusGUId is valid in the sense of "does not need to be replaced".
     * This is the case if its available and not dirty and expired.
     */
    isIgusGUIdValid() {
        const igusGUId = this.getCurrentIgusGUId();
        if (!igusGUId)
            return false;
        return Boolean(igusGUId.id) && !igusGUId.dirty && !igusGUId.expired;
    }
    /**
     * Return a clone of the current IgusGUId
     */
    getIgusGUIdObjClone() {
        return (0, echain_shared_ts_utils_1.clonePrimitive)(this.getCurrentIgusGUId());
    }
    set _iaZeichnungsnummer(value) {
        this.model.IAZeichnungsnummer = value;
    }
    get _iaZeichnungsnummer() {
        return this.model.IAZeichnungsnummer;
    }
    set iaZeichnungsnummer(value) {
        this.model.IAZeichnungsnummer = value;
    }
    get iaZeichnungsnummer() {
        return this.model.IAZeichnungsnummer;
    }
    set _bezeichnung(value) {
        this.model.Bezeichnung = value;
    }
    get _bezeichnung() {
        return this.model.Bezeichnung;
    }
    set bezeichnung(value) {
        this.model.Bezeichnung = value;
    }
    get bezeichnung() {
        return this.model.Bezeichnung;
    }
    set leitungen(value) {
        this.model.Leitungen = value.toModel();
        this.model.LeitungBomBehavior = value.bomBehavior;
    }
    get leitungen() {
        const leitungen = echain_shared_articles_oo_1.LeitungList.fromModel(this.model.Leitungen);
        if (this.model.LeitungBomBehavior) {
            leitungen.bomBehavior = this.model.LeitungBomBehavior;
        }
        return leitungen;
    }
    /** Retrieves a clone of the `LeitungList` */
    getLeitungenClone() {
        return this.leitungen.clone();
    }
    /** Retrieves the count of leitungen */
    getLeitungenCount() {
        return this.model.Leitungen.length;
    }
    /** Retrieves whether any `Leitung` is set */
    hasAnyLeitung() {
        // TODO: EKE-12946 check if this applies here too
        return this.leitungen.count() > 0;
    }
    /** Retrieves whether are no `Leitungen` set (convenience method for `!hasAnyLeitung()`) */
    hasNoLeitungen() {
        // TODO: EKE-12946 check if this applies here too
        return !this.hasAnyLeitung();
    }
    /** Retrieves whether any `non-placeholder Leitung` is set */
    hasAnyNonPlaceholderLeitung() {
        return this.leitungen.filter((leitung) => !leitung.isPlaceholder).count() > 0;
    }
    /**
     * Checks if Configuration has unplaced Leitungen
     */
    hasUnplacedLeitungen() {
        // TODO: EKE-12946 check if this applies here too
        return this.leitungen.getUnplacedLeitungArray().length > 0;
    }
    /**
     * Checks if Configuration has only unplaced Leitungen
     */
    hasOnlyUnplacedLeitungen() {
        // TODO: EKE-12946 check if this applies here too
        return !this.leitungen.getPlacedLeitungArray().length;
    }
    /**
     * Checks if Configuration has only unplaced Leitungen and BomBehavior is UseAll
     */
    hasOnlyUnplacedLeitungenAndUseAll() {
        return this.hasOnlyUnplacedLeitungen() && this.leitungen.bomBehavior === echain_shared_articles_oo_1.LeitungBomBehavior.UseAll;
    }
    /**
     * Retrieves whether ONLY `placeholder Leitungen` are set. Returns false if there are no
     * `Leitungen`
     */
    hasOnlyPlaceholderLeitungen() {
        const leitungenArray = this.leitungen.toArray();
        for (const leitung of leitungenArray) {
            if (!leitung.isPlaceholder) {
                return false;
            }
        }
        return leitungenArray.length > 0;
    }
    /** Retrieves whether Verpackung and Konfektionierung are NOT applicable */
    isNotVerpackungKonfektionierungApplicable() {
        return (this.isEspoolConfig() ||
            this.hasLeitungenLoseBeilegen() ||
            !this.hasAnyLeitung() ||
            this.hasOnlyPlaceholderLeitungen()
        // TODO: EKE-11330 clarify harnessing
        // this.hasOnlyUnplacedLeitungenAndUseAll() ||
        // this.hasOnlyUnplacedLeitungen()
        );
    }
    /** Retrieves whether Verpackung and Konfektionierung are applicable */
    isVerpackungKonfektionierungApplicable() {
        return !this.isNotVerpackungKonfektionierungApplicable();
    }
    /** Retrieves whether a specific `Leitung` is set */
    hasLeitung(leitung) {
        return this.leitungen.has(leitung);
    }
    set kette(value) {
        this.model.Kette = value === null || value === void 0 ? void 0 : value.toModel();
    }
    get kette() {
        return echain_shared_articles_oo_1.A.optional(this.model.Kette);
    }
    /** Retrieves a clone of the `Kette` */
    getKetteClone() {
        return this.kette ? this.kette.clone() : undefined;
    }
    /** Retrieves whether any `Kette` is set */
    hasAnyKette() {
        return this.kette != null;
    }
    /**
     * Retrieves whether any `Kette` with an artNr is set.
     */
    hasAnyKetteWithArtNr() {
        var _a;
        return !!((_a = this.kette) === null || _a === void 0 ? void 0 : _a.artNr);
    }
    /** Retrieves whether any `Kette` is generated and thus useable for further configuration */
    hasGeneratedKette() {
        return (0, echain_shared_articles_oo_1.isGeneratedArticle)(this.kette);
    }
    /** Retrieves whether any `Espool` is generated and thus useable for further configuration */
    hasGeneratedEspool() {
        return (0, echain_shared_articles_oo_1.isGeneratedArticle)(this.espool);
    }
    /** Retrieves whether any Triflex `Kette` is set */
    hasAnyTriflexKette() {
        return Boolean(this.kette && this.kette.isSerieTriflexR());
    }
    /** Retrieves whether any Twisterband `Kette` is set */
    hasAnyTwisterbandKette() {
        return Boolean(this.kette && this.kette.isSerieTwisterband());
    }
    /** Retrieves whether a specific `Kette` is set */
    hasKette(kette) {
        return Boolean(this.kette && this.kette.is(kette));
    }
    set espool(value) {
        this.model.Espool = value === null || value === void 0 ? void 0 : value.toModel();
    }
    get espool() {
        return echain_shared_articles_oo_1.A.optional(this.model.Espool);
    }
    /** Retrieves a clone of the `espool` */
    getEspoolClone() {
        var _a;
        return (_a = this.espool) === null || _a === void 0 ? void 0 : _a.clone();
    }
    isEspoolConfig() {
        return Boolean(this.espool);
    }
    set aes(value) {
        this.model.AEs = value.toModel();
    }
    get aes() {
        return echain_shared_articles_oo_1.AEList.fromModel(this.model.AEs);
    }
    /** Retrieves a clone of the `AEList` */
    getAEsClone() {
        return this.aes.clone();
    }
    /** Retrieves the count of AEs */
    getAEsCount() {
        return this.aes.count();
    }
    /** Retrieves whether any `AE` is set */
    hasAnyAE() {
        return this.aes.count() > 0;
    }
    /** Retrieves whether a specific `AE` is set */
    hasAE(ae) {
        return this.aes.has(ae);
    }
    set _hasNoAE(value) {
        this.model.HasNoAE = value;
    }
    get _hasNoAE() {
        return this.model.HasNoAE;
    }
    hasNoAE() {
        return this._hasNoAE;
    }
    set _hasNoRinne(value) {
        this.model.HasNoRinne = value;
    }
    get _hasNoRinne() {
        return this.model.HasNoRinne;
    }
    hasNoRinne() {
        return this._hasNoRinne;
    }
    set ias(value) {
        this.model.IAs = value.toModel();
    }
    get ias() {
        return echain_shared_articles_oo_1.IAList.fromModel(this.model.IAs);
    }
    /** Retrieves a clone of the `IAList` */
    getIAsClone() {
        return this.ias.clone();
    }
    /** Retrieves the count of IAs */
    getIAsCount() {
        return this.ias.count();
    }
    /**
     * retrieves IAs with Beat
     */
    getIAsByBeat(iaBeat) {
        return this.ias.filter((ia) => ia.startingAtNthKettenglied === iaBeat.startingAt && ia.perNthKettenglied === iaBeat.perNth);
    }
    /** Retrieves whether any `IA` is set */
    hasAnyIA() {
        return this.ias.count() > 0;
    }
    /** Retrieves whether a specifc `IA` is set */
    hasIA(ia) {
        return this.ias.has(ia);
    }
    set zugentlastungen(value) {
        this.model.Zugentlastungen = value.toModel();
    }
    get zugentlastungen() {
        return echain_shared_articles_oo_1.ZugentlastungList.fromModel(this.model.Zugentlastungen);
    }
    /** Retrieves a clone of the `ZugentlastungList` */
    getZugentlastungenClone() {
        return this.zugentlastungen.clone();
    }
    /** Retrieves the count of zugentlastungen */
    getZugentlastungenCount() {
        return this.zugentlastungen.count();
    }
    /** Retrieves whether any `Zugentlastung` is set */
    hasAnyZugentlastung() {
        return this.zugentlastungen.count() > 0;
    }
    /** Retrieves whether a specific `Zugentlastung` is set */
    hasZugentlastung(ze) {
        return this.zugentlastungen.has(ze);
    }
    set verpackungen(value) {
        this.model.Verpackungen = value.toModel();
    }
    get verpackungen() {
        return echain_shared_articles_oo_1.VerpackungList.fromModel(this.model.Verpackungen);
    }
    /** Retrieves a clone of the `VerpackungList` */
    getVerpackungenClone() {
        return this.verpackungen.clone();
    }
    /** Retrieves the count of verpackungen */
    getVerpackungenCount() {
        return this.verpackungen.count();
    }
    /** Retrieves whether any `Verpackung` is set */
    hasAnyVerpackung() {
        return this.verpackungen.count() > 0;
    }
    /** Retrieves whether any `Verpackung` is set */
    hasVerpackung(v) {
        return this.verpackungen.has(v);
    }
    set konfektionierungen(value) {
        this.model.Konfektionierungen = value.toModel();
    }
    get konfektionierungen() {
        return echain_shared_articles_oo_1.KonfektionierungList.fromModel(this.model.Konfektionierungen);
    }
    /** Retrieves a clone of the `KonfektionierungList` */
    getKonfektionierungenClone() {
        return this.konfektionierungen.clone();
    }
    /** Retrieves the count of konfektionierungen */
    getKonfektionierungenCount() {
        return this.konfektionierungen.count();
    }
    /** Retrieves whether any `Konfektionierung` is set */
    hasAnyKonfektionierung() {
        return this.konfektionierungen.toArray().length > 0;
    }
    set fuehrungsrinnen(value) {
        this.model.Fuehrungsrinnen = value.toModel();
    }
    get fuehrungsrinnen() {
        return echain_shared_articles_oo_1.FuehrungsrinneList.fromModel(this.model.Fuehrungsrinnen);
    }
    /** Retrieves a clone of the `FuehrungsrinneList` */
    getFuehrungsrinnenClone() {
        return this.fuehrungsrinnen.clone();
    }
    /** Retrieves the count of fuehrungsrinnen */
    getFuehrungsrinnenCount() {
        return this.fuehrungsrinnen.count();
    }
    /** Retrieves whether any `Fuehrungsrinne` is set */
    hasAnyFuehrungsrinne() {
        return this.fuehrungsrinnen.count() > 0;
    }
    /** Retrieves whether a specific `Fuehrungsrinne` is set */
    hasFuehrungsrinne(fuehrungsrinne) {
        return this.fuehrungsrinnen.has(fuehrungsrinne);
    }
    set montageSets(value) {
        this.model.MontageSets = value.toModel();
    }
    get montageSets() {
        return echain_shared_articles_oo_1.MontageSetList.fromModel(this.model.MontageSets);
    }
    /** Retrieves a clone of the `MontageSetList` */
    getMontageSetsClone() {
        return this.montageSets.clone();
    }
    /** Retrieves the count of montageSets */
    getMontageSetsCount() {
        return this.montageSets.count();
    }
    /** Retrieves whether any `MontageSet` is set */
    hasAnyMontageSet() {
        return this.montageSets.count() > 0;
    }
    /** Retrieves whether a specific `MontageSet` is set */
    hasMontageSet(montageSet) {
        return this.montageSets.has(montageSet);
    }
    set montBes(value) {
        this.model.MontBes = value.toModel();
    }
    get montBes() {
        return echain_shared_articles_oo_1.MontBesList.fromModel(this.model.MontBes);
    }
    /** Retrieves a clone of the `MontBesList` */
    getMontBesClone() {
        return this.montBes.clone();
    }
    /** Retrieves the count of `MontBesList` */
    getMontBesCount() {
        return this.montBes.count();
    }
    /** Retrieves whether any `MontBesList` is set */
    hasAnyMontBes() {
        return this.montBes.count() > 0;
    }
    /** Retrieves whether a specific `MontBes` is set */
    hasMontBes(montBes) {
        return this.montBes.has(montBes);
    }
    get customKonfektionierungPrice() {
        return this.model.CustomKonfektionierungPrice;
    }
    set customKonfektionierungPrice(value) {
        this.model.CustomKonfektionierungPrice = value;
    }
    /**
     * Retrieves whether a custom price is set or not.
     */
    hasCustomKonfektionierungPrice() {
        return this.customKonfektionierungPrice != null;
    }
    /**
     * Retrieves a clone of the custom konfektionierung price
     */
    getCustomKonfektionierungPriceClone() {
        return (0, echain_shared_ts_utils_1.clonePrimitive)(this.customKonfektionierungPrice);
    }
    get fileRefs() {
        return this.model.FileRefs.map((v) => echain_shared_various_1.FileRef.fromModel(v));
    }
    set fileRefs(values) {
        this.model.FileRefs = values.map((v) => v.toModel());
    }
    /**
     * Retrieves a clone of the file references.
     */
    getFileRefsClone() {
        if (!this.fileRefs) {
            throw new Error(`Configuration.getFileRefsClone: this.fileRefs is undefined`);
        }
        return this.fileRefs.map((fileRef) => fileRef.clone());
    }
    /**
     * Retrieves whether a file ref exists or not.
     */
    existsFileRef(fileRef) {
        if (!this.fileRefs) {
            throw new Error(`Configuration.existsFileRef: this.fileRefs is undefined`);
        }
        return !!this.fileRefs.find((ref) => {
            return (0, echain_shared_ts_utils_1.deepEqual)(ref, fileRef);
        });
    }
    get pdfRefs() {
        return this.model.PdfRefs.map((v) => echain_shared_various_1.PdfRef.fromModel(v));
    }
    set pdfRefs(values) {
        this.model.PdfRefs = values.map((v) => v.toModel());
    }
    /**
     * Retrieves a clone of the pdf references.
     */
    getPdfRefsClone() {
        if (!this.pdfRefs) {
            throw new Error(`Configuration.getPdfRefsClone: this.pdfRefs is undefined`);
        }
        return this.pdfRefs.map((pdfRef) => pdfRef.clone());
    }
    /**
     * Retrieves whether any pdf reference is set or not.
     */
    hasAnyPdfRef() {
        if (!this.pdfRefs) {
            throw new Error(`Configuration.getPdfRefsClone: this.pdfRefs is undefined`);
        }
        return !!this.pdfRefs.length;
    }
    get quoteCount() {
        return this.model.QuoteCount;
    }
    set quoteCount(value) {
        this.model.QuoteCount = value;
    }
    getQuoteCount() {
        return this.quoteCount;
    }
    get maxLeitungsDurchmesser() {
        return echain_shared_quantities_1.Q.optional(this.model.MaxLeitungsDurchmesser);
    }
    set maxLeitungsDurchmesser(value) {
        this.model.MaxLeitungsDurchmesser = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getMaxLeitungsDurchmesserClone() {
        return this.maxLeitungsDurchmesser ? this.maxLeitungsDurchmesser.clone() : undefined;
    }
    get minLeitungsBiegeradius() {
        return echain_shared_quantities_1.Q.optional(this.model.MinLeitungsBiegeradius);
    }
    set minLeitungsBiegeradius(value) {
        this.model.MinLeitungsBiegeradius = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getMinLeitungsBiegeradiusClone() {
        return this.minLeitungsBiegeradius ? this.minLeitungsBiegeradius.clone() : undefined;
    }
    get einbauart() {
        return this.model.Einbauart;
    }
    set einbauart(value) {
        this.model.Einbauart = value;
    }
    getEinbauart() {
        return this.einbauart;
    }
    get zusatzlast() {
        return echain_shared_quantities_1.Q.optional(this.model.Zusatzlast);
    }
    set zusatzlast(value) {
        this.model.Zusatzlast = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getZusatzlastClone() {
        return this.zusatzlast ? this.zusatzlast.clone() : undefined;
    }
    get _hasBogenInFreitragenderAnwendung() {
        return this.model.HasBogenInFreitragenderAnwendung;
    }
    set _hasBogenInFreitragenderAnwendung(value) {
        this.model.HasBogenInFreitragenderAnwendung = value;
    }
    hasBogenInFreitragenderAnwendung() {
        return this._hasBogenInFreitragenderAnwendung;
    }
    get geschwindigkeitMax() {
        return echain_shared_quantities_1.Q.optional(this.model.GeschwindigkeitMax);
    }
    set geschwindigkeitMax(value) {
        this.model.GeschwindigkeitMax = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getGeschwindigkeitMaxClone() {
        return this.geschwindigkeitMax ? this.geschwindigkeitMax.clone() : undefined;
    }
    get beschleunigungMax() {
        return echain_shared_quantities_1.Q.optional(this.model.BeschleunigungMax);
    }
    set beschleunigungMax(value) {
        this.model.BeschleunigungMax = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getBeschleunigungMaxClone() {
        return this.beschleunigungMax ? this.beschleunigungMax.clone() : undefined;
    }
    get bauraumhoehe() {
        return echain_shared_quantities_1.Q.optional(this.model.Bauraumhoehe);
    }
    set bauraumhoehe(value) {
        this.model.Bauraumhoehe = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getBauraumhoeheClone() {
        return this.bauraumhoehe ? this.bauraumhoehe.clone() : undefined;
    }
    get bauraumlaenge() {
        return echain_shared_quantities_1.Q.optional(this.model.Bauraumlaenge);
    }
    set bauraumlaenge(value) {
        this.model.Bauraumlaenge = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getBauraumlaengeClone() {
        return this.bauraumlaenge ? this.bauraumlaenge.clone() : undefined;
    }
    get bauraumbreite() {
        return echain_shared_quantities_1.Q.optional(this.model.Bauraumbreite);
    }
    set bauraumbreite(value) {
        this.model.Bauraumbreite = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getBauraumbreiteClone() {
        return this.bauraumbreite ? this.bauraumbreite.clone() : undefined;
    }
    get maxAussenhoehe() {
        return echain_shared_quantities_1.Q.optional(this.model.MaxAussenhoehe);
    }
    set maxAussenhoehe(value) {
        this.model.MaxAussenhoehe = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getMaxAussenhoeheClone() {
        return this.maxAussenhoehe ? this.maxAussenhoehe.clone() : undefined;
    }
    get minInnenhoehe() {
        return echain_shared_quantities_1.Q.optional(this.model.MinInnenhoehe);
    }
    set minInnenhoehe(value) {
        this.model.MinInnenhoehe = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getMinInnenhoeheClone() {
        return this.minInnenhoehe ? this.minInnenhoehe.clone() : undefined;
    }
    get minInnenbreite() {
        return echain_shared_quantities_1.Q.optional(this.model.MinInnenbreite);
    }
    set minInnenbreite(value) {
        this.model.MinInnenbreite = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getMinInnenbreiteClone() {
        return this.minInnenbreite ? this.minInnenbreite.clone() : undefined;
    }
    get minKettenBiegeradius() {
        return echain_shared_quantities_1.Q.optional(this.model.MinKettenBiegeradius);
    }
    set minKettenBiegeradius(value) {
        this.model.MinKettenBiegeradius = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getMinKettenBiegeradiusClone() {
        return this.minKettenBiegeradius ? this.minKettenBiegeradius.clone() : undefined;
    }
    get doppelhuebe() {
        return echain_shared_quantities_1.Q.optional(this.model.Doppelhuebe);
    }
    set doppelhuebe(value) {
        this.model.Doppelhuebe = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getDoppelhuebeClone() {
        return this.doppelhuebe ? this.doppelhuebe.clone() : undefined;
    }
    get _hasQuerbeschleunigung() {
        return this.model.HasQuerbeschleunigung;
    }
    set _hasQuerbeschleunigung(value) {
        this.model.HasQuerbeschleunigung = value;
    }
    hasQuerbeschleunigung() {
        return this._hasQuerbeschleunigung;
    }
    get allowedBauformen() {
        return this.model.AllowedBauformen ? new Set(this.model.AllowedBauformen) : undefined;
    }
    set allowedBauformen(value) {
        this.model.AllowedBauformen = value ? Array.from(value) : undefined;
    }
    getAllowedBauformenClone() {
        return this.allowedBauformen != null ? new Set(this.allowedBauformen) : undefined;
    }
    get allowedKetteOpeningPrinciples() {
        return this.model.AllowedKetteOpeningPrinciples ? new Set(this.model.AllowedKetteOpeningPrinciples) : undefined;
    }
    set allowedKetteOpeningPrinciples(value) {
        this.model.AllowedKetteOpeningPrinciples = value ? Array.from(value) : undefined;
    }
    getAllowedKetteOpeningPrinciplesClone() {
        return this.allowedKetteOpeningPrinciples != null ? new Set(this.allowedKetteOpeningPrinciples) : undefined;
    }
    get leitungenLoseBeilegen() {
        return this.model.LeitungenLoseBeilegen;
    }
    set leitungenLoseBeilegen(value) {
        this.model.LeitungenLoseBeilegen = value;
    }
    hasLeitungenLoseBeilegen() {
        return this.leitungenLoseBeilegen;
    }
    get minEnvironmentTemperatur() {
        return echain_shared_quantities_1.Q.optional(this.model.MinEnvironmentTemperatur);
    }
    set minEnvironmentTemperatur(value) {
        this.model.MinEnvironmentTemperatur = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getMinEnvironmentTemperaturClone() {
        return this.minEnvironmentTemperatur ? this.minEnvironmentTemperatur.clone() : undefined;
    }
    get maxEnvironmentTemperatur() {
        return echain_shared_quantities_1.Q.optional(this.model.MaxEnvironmentTemperatur);
    }
    set maxEnvironmentTemperatur(value) {
        this.model.MaxEnvironmentTemperatur = value === null || value === void 0 ? void 0 : value.toJson();
    }
    getMaxEnvironmentTemperaturClone() {
        return this.maxEnvironmentTemperatur ? this.maxEnvironmentTemperatur.clone() : undefined;
    }
    get averageEnvironmentTemperatur() {
        return (0, echain_shared_quantities_1.Q)(this.model.AverageEnvironmentTemperatur);
    }
    set averageEnvironmentTemperatur(value) {
        this.model.AverageEnvironmentTemperatur = value.toJson();
    }
    getAverageEnvironmentTemperaturClone() {
        return this.averageEnvironmentTemperatur.clone();
    }
    get _isIgnoreSafetyDistance() {
        return this.model.IsIgnoreSafetyDistance;
    }
    set _isIgnoreSafetyDistance(value) {
        this.model.IsIgnoreSafetyDistance = value;
    }
    get isIgnoreSafetyDistance() {
        return this._isIgnoreSafetyDistance;
    }
    get _useSameIAColorAndMaterial() {
        return this.model.UseSameIAColorAndMaterial;
    }
    get useSameIAColorAndMaterial() {
        return this._useSameIAColorAndMaterial;
    }
    get _isMinBiegeradiusFilterDisabled() {
        return this.model.IsMinBiegeradiusFilterDisabled;
    }
    set _isMinBiegeradiusFilterDisabled(value) {
        this.model.IsMinBiegeradiusFilterDisabled = value;
    }
    get isMinBiegeradiusFilterDisabled() {
        return this._isMinBiegeradiusFilterDisabled;
    }
    get _isStackingOfLeitungenAllowed() {
        return this.model.IsStackingOfLeitungenAllowed;
    }
    set _isStackingOfLeitungenAllowed(value) {
        this.model.IsStackingOfLeitungenAllowed = value;
    }
    get isStackingOfLeitungenAllowed() {
        return this._isStackingOfLeitungenAllowed;
    }
    get _appliedIaFillingScenarioId() {
        return this.model.AppliedIaFillingScenarioId;
    }
    set _appliedIaFillingScenarioId(value) {
        this.model.AppliedIaFillingScenarioId = value;
    }
    get appliedIaFillingScenarioId() {
        return this._appliedIaFillingScenarioId;
    }
    get _userGroups() {
        return this.model.UserGroups;
    }
    set _userGroups(value) {
        this.model.UserGroups = value;
    }
    get userGroups() {
        return this._userGroups;
    }
    get _isManualMode() {
        return this.model.IsManualMode;
    }
    set _isManualMode(value) {
        this.model.IsManualMode = value;
    }
    get isManualMode() {
        return this._isManualMode;
    }
    get _specificArticles() {
        return this.model.SpecificArticles;
    }
    set _specificArticles(value) {
        this.model.SpecificArticles = value;
    }
    get specificArticles() {
        return this._specificArticles;
    }
    set ketteFilterProperties(value) {
        this.model.KetteFilterProperties = value;
    }
    get ketteFilterProperties() {
        return this.model.KetteFilterProperties;
    }
    get _leitungFilterProperties() {
        return this.model.LeitungFilterProperties;
    }
    set _leitungFilterProperties(value) {
        this.model.LeitungFilterProperties = value;
    }
    get leitungFilterProperties() {
        return this._leitungFilterProperties;
    }
    get _iaArrangement() {
        return this.model.IaArrangement;
    }
    set _iaArrangement(value) {
        this.model.IaArrangement = value;
    }
    get iaArrangement() {
        return this._iaArrangement;
    }
    get children() {
        var _a;
        return (_a = this.model.Children) === null || _a === void 0 ? void 0 : _a.map((c) => Configuration.fromModel(c));
    }
    set children(value) {
        this.model.Children = value === null || value === void 0 ? void 0 : value.map((v) => v.toModel());
    }
    get childRelation() {
        return this.model.ChildRelation;
    }
    set childRelation(value) {
        this.model.ChildRelation = value;
    }
    constructor() {
        this.className = 'Configuration';
        /** the maximum count of `montBes` items that can be added to a configuration */
        this.maxMontBesCount = 10;
        this.averageEnvironmentTemperaturDefault_celsius = 20;
        this.uniqueId = this.generateUUID();
        this.model = {
            AEs: [],
            AverageEnvironmentTemperatur: echain_shared_quantities_1.Temperature.celsius(this.averageEnvironmentTemperaturDefault_celsius).toJson(),
            Bezeichnung: undefined,
            DomainVersion: (0, version_1.echainDomainVersion)(),
            FileRefs: [],
            Fuehrungsrinnen: [],
            IAs: [],
            IaArrangement: echain_shared_igus_fundamentals_1.IaArrangement.single,
            IsAlienConfiguration: false,
            IsIgnoreSafetyDistance: false,
            IsStackingOfLeitungenAllowed: false,
            IgusGUIds: [],
            Konfektionierungen: [],
            Kette: undefined,
            Leitungen: [],
            LeitungBomBehavior: echain_shared_articles_oo_1.LeitungBomBehavior.UseAll,
            MontBes: [],
            MontageSets: [],
            PdfRefs: [],
            QuoteCount: 1,
            UseSameIAColorAndMaterial: false,
            Verpackungen: [],
            Zugentlastungen: [],
        };
    }
    static fromScratch() {
        return new Configuration();
    }
    static fromJson(json) {
        const configuration = Configuration.fromScratch();
        configuration.fromJson(json);
        return configuration;
    }
    static fromModel(model) {
        const configuration = new Configuration();
        Object.assign(configuration.model, model);
        Object.assign(model, configuration.model);
        configuration.model = model;
        return configuration;
    }
    fromScratch() {
        throw new Error(`${this.className}.fromScratch not implemented`);
    }
    fromJson(json, cloneStrategy = 'deep', deepCloneSanitizeOptions = { removeNully: true }) {
        var _a;
        const migrationResult = echain_shared_various_1.MigrationService.process(json, echain_shared_configurations_1.configurationMigrations);
        const validation = echain_shared_validation_1.Validation.empty();
        const clonedJson = this.cloneJsonIntoModel(migrationResult.json, cloneStrategy, deepCloneSanitizeOptions);
        // patch clonedJson by calling all article accessors (which will ensure that mandatory props are set)
        const articleToFromJsonMap = {
            AEs: echain_shared_articles_oo_1.AEList.fromJson,
            Espool: echain_shared_articles_oo_1.Espool.fromJson,
            Fuehrungsrinnen: echain_shared_articles_oo_1.FuehrungsrinneList.fromJson,
            Kette: echain_shared_articles_oo_1.Kette.fromJson,
            Konfektionierungen: echain_shared_articles_oo_1.KonfektionierungList.fromJson,
            Leitungen: echain_shared_articles_oo_1.LeitungList.fromJson,
            IAs: echain_shared_articles_oo_1.IAList.fromJson,
            MontBes: echain_shared_articles_oo_1.MontBesList.fromJson,
            MontageSets: echain_shared_articles_oo_1.MontageSetList.fromJson,
            Verpackungen: echain_shared_articles_oo_1.VerpackungList.fromJson,
            Zugentlastungen: echain_shared_articles_oo_1.ZugentlastungList.fromJson,
        };
        Object.entries(articleToFromJsonMap).forEach(([key, factory]) => {
            const typedKey = key;
            if (clonedJson[typedKey]) {
                clonedJson[typedKey] = factory(clonedJson[typedKey]).toModel();
            }
        });
        // patch child configurations (and ensure all migrations are applied)
        clonedJson.Children = (_a = clonedJson.Children) === null || _a === void 0 ? void 0 : _a.map((childJson) => Configuration.fromJson(childJson).toModel());
        if ((0, echain_shared_configurations_1.isConfigurationModel)(clonedJson, validation)) {
            this.model = clonedJson;
        }
        else {
            (0, echain_shared_logging_1.getLogger)().warn(`${this.className}.fromJson() failed`, { json, validation });
        }
        return validation;
    }
    cloneJsonIntoModel(json, cloneStrategy, deepCloneSanitizeOptions) {
        switch (cloneStrategy) {
            case 'shallow':
                return Object.assign(Object.assign({}, this.model), json);
            case 'deep':
            default:
                return Object.assign(Object.assign({}, this.model), (0, echain_shared_ts_utils_1.clonePrimitive)(json, deepCloneSanitizeOptions));
            // todo outcomment this after removing Serialization
            // default:
            //   assertUnreachable(cloneStrategy);
        }
    }
    toJson(sanitizeOptions = { removeNully: true }) {
        var _a, _b;
        const clonedModel = (0, echain_shared_ts_utils_1.clonePrimitive)(this.model, sanitizeOptions);
        if ((_a = this.getCurrentIgusGUId()) === null || _a === void 0 ? void 0 : _a.id) {
            clonedModel.IgusGUId = (_b = this.getCurrentIgusGUId()) === null || _b === void 0 ? void 0 : _b.id;
        }
        return clonedModel;
    }
    toJsonWithoutSensitiveData() {
        const configJson = this.toJson();
        delete configJson.IAAuthorName;
        delete configJson.IACompanyId;
        delete configJson.IACompanyName;
        return configJson;
    }
    toModel() {
        return this.model;
    }
    clone() {
        throw new Error(`${this.className}.clone not implemented`);
    }
    duplicate() {
        throw new Error(`${this.className}.duplicate not# implemented`);
    }
    /*************************************************************************************************
     * Setters
     */
    /** Clears all props without losing the ref to model */
    _clear() {
        // clear all props
        for (const prop of Object.getOwnPropertyNames(this.model)) {
            delete this.model[prop];
        }
        // create a new one
        const configurationFromScratch = this.fromScratch();
        // perform migration on new one
        const model = configurationFromScratch.toModel();
        configurationFromScratch.fromJson(model);
        // assign into our model
        Object.assign(this.model, configurationFromScratch.toModel());
    }
    _clearExceptLeitungen() {
        const leitungen = this.leitungen;
        this._clear();
        this.leitungen = leitungen;
    }
    _removeAllArticles() {
        this._removeLeitungen();
        this._removeKette();
        this._removeEspool();
        this._removeAEs();
        this._removeIAs();
        this._removeZugentlastungen();
        this._removeVerpackungen();
        this._removeKonfektionierungen();
        this._removeFuehrungsrinnen();
        this._removeMontageSets();
        this._removeAllMontBes();
        this._clearCustomKonfektionierungPrice();
        this._removeSmartPlasticsConfiguration();
    }
    removeAllArticlesExceptLeitungen() {
        return this._removeAllArticlesExceptLeitungen();
    }
    _removeAllArticlesExceptLeitungen() {
        if (this.espool) {
            this._removeEspool();
        }
        else {
            this._removeKette();
        }
        this._removeAEs();
        this._removeIAs();
        this._removeZugentlastungen();
        this._removeVerpackungen();
        this._removeKonfektionierungen();
        this._removeFuehrungsrinnen();
        this._removeMontageSets();
        this._removeAllMontBes();
        this._clearCustomKonfektionierungPrice();
        this._removeIAArrangement();
        this._removeSmartPlasticsConfiguration();
    }
    //-----------------------
    // Leitung
    _addLeitung(leitung, options = {}) {
        if (!leitung)
            throw new Error('Configuration._addLeitung: invalid leitung');
        if (this.leitungen.count() >= echain_shared_configurations_1.CONFIGURATION_LEITUNG_MAX)
            throw new Error('Configuration._addLeitung: max amount of ' + echain_shared_configurations_1.CONFIGURATION_LEITUNG_MAX + ' items reached: ');
        if (options && options.after) {
            this.leitungen.addAfter(leitung.clone(), options.after);
        }
        else {
            this.leitungen.add(leitung.clone());
        }
    }
    _replaceLeitung(oldLeitung, newLeitung) {
        if (!oldLeitung)
            throw new Error('Configuration._replaceLeitung: invalid old leitung');
        if (!newLeitung)
            throw new Error('Configuration._replaceLeitung: invalid new leitung');
        this.leitungen.replace(oldLeitung, newLeitung);
    }
    _updateLeitung(leitung) {
        if (!leitung)
            throw new Error('Configuration._updateLeitung: invalid leitung');
        this.leitungen.update(leitung.clone());
    }
    _removeLeitung(leitung) {
        if (!leitung)
            throw new Error('Configuration._removeLeitung: invalid leitung');
        this.leitungen.remove(leitung);
    }
    _removeLeitungen() {
        this.leitungen.removeAll();
    }
    removeLeitungIAPositions() {
        return this._removeLeitungIAPositions();
    }
    _removeLeitungIAPositions() {
        this.leitungen.toArray().forEach((leitung) => (leitung.localTransform = undefined));
    }
    _removeLeitungIAIndices() {
        this.leitungen.removeAllIAIndices();
    }
    //-----------------------
    // Kette
    _setKette(kette) {
        if (!kette)
            throw new Error('Configuration._setKette: invalid kette');
        this.kette = kette.clone();
    }
    _removeKette() {
        this.kette = undefined;
    }
    //-----------------------
    // espool
    setEspool(espool) {
        return this._setEspool(espool);
    }
    _setEspool(espool) {
        if (!espool)
            throw new Error('Configuration._setEspool: invalid espool');
        this.espool = espool.clone();
    }
    _removeEspool() {
        this.espool = undefined;
        this.childRelation = undefined;
        this.children = undefined;
    }
    //-----------------------
    // AEs
    _addAE(ae) {
        if (!ae)
            throw new Error('Configuration._addAE: invalid ae');
        this.aes.add(ae.clone());
    }
    _removeAE(ae) {
        if (!ae)
            throw new Error('Configuration._removeAE: invalid ae');
        this.aes.remove(ae);
    }
    _removeAEs() {
        this.aes.removeAll();
    }
    //-----------------------
    // IAs
    _addIA(ia) {
        if (!ia)
            throw new Error('Configuration._addIA: invalid ia');
        // handle missing artNrGenerated on-the-fly
        // assuming that '0' is the default color for IAs
        if (!ia.artNrGenerated) {
            if (!ia.artNr)
                throw new Error('Configuration._addIA: trying to generate an artNrGenerated but we have no artNr');
            ia.artNrGenerated = echain_shared_articles_oo_1.IA.generateArtNr(echain_shared_i18n_1.LocaleService.getArtNrCountry(), ia.artNr, ia.color || '0', ia.material);
        }
        this.ias.add(ia.clone());
    }
    _removeIA(ia) {
        if (!ia)
            throw new Error('Configuration._removeIA: invalid ia');
        this.ias.remove(ia);
    }
    _removeIAIndices() {
        if (!this.ias)
            return;
        for (const ia of this.ias.toArray()) {
            ia.setIAIndex(undefined);
        }
    }
    _removeIAs() {
        this.ias.removeAll();
    }
    //-----------------------
    // Zugentlastung
    _addZugentlastung(zugentlastung) {
        if (!zugentlastung)
            throw new Error('Configuration._addZugentlastung: invalid zugentlastung');
        this.zugentlastungen.add(zugentlastung.clone());
    }
    _removeZugentlastung(zugentlastung) {
        if (!zugentlastung)
            throw new Error('Configuration._removeZugentlastung: invalid zugentlastung');
        this.zugentlastungen.remove(zugentlastung);
    }
    _removeZugentlastungen() {
        this.zugentlastungen.removeAll();
    }
    //-----------------------
    // Verpackungen
    _addVerpackung(verpackung) {
        if (!verpackung)
            throw new Error('Configuration._addVerpackung: invalid verpackung');
        this.verpackungen.add(verpackung.clone());
    }
    _removeVerpackung(verpackung) {
        if (!verpackung)
            throw new Error('Configuration._removeVerpackung: invalid verpackung');
        this.verpackungen.remove(verpackung);
    }
    _removeVerpackungen() {
        this.verpackungen.removeAll();
    }
    //-----------------------
    // Konfektionierungen
    _addKonfektionierung(konfektionierung) {
        if (!konfektionierung)
            throw new Error('Configuration._addKonfektionierung: invalid konfektionierung');
        this.konfektionierungen.add(konfektionierung.clone());
    }
    _removeKonfektionierungen() {
        this.konfektionierungen.removeAll();
    }
    _setCustomKonfektionierungPrice(price) {
        if (!price)
            throw new Error('Configuration._setCustomKonfektionierungPrice: invalid price');
        this.customKonfektionierungPrice = (0, echain_shared_ts_utils_1.clonePrimitive)(price);
    }
    _clearCustomKonfektionierungPrice() {
        this.customKonfektionierungPrice = undefined;
    }
    //-----------------------
    // Fuherungsrinnen
    _addFuehrungsrinne(fuehrungsrinne) {
        if (!fuehrungsrinne)
            throw new Error('Configuration._addFuehrungsrinne: invalid fuehrungsrinne');
        this.fuehrungsrinnen.add(fuehrungsrinne.clone());
    }
    _removeFuehrungsrinne(fuehrungsrinne) {
        if (!fuehrungsrinne)
            throw new Error('Configuration._removeFuehrungsrinne: invalid fuehrungsrinne');
        this.fuehrungsrinnen.remove(fuehrungsrinne);
    }
    _removeFuehrungsrinnen() {
        this.fuehrungsrinnen.removeAll();
    }
    //-----------------------
    // MontageSets
    _addMontageSet(montageSet) {
        if (!montageSet)
            throw new Error('Configuration._addMontageSet: invalid montageSet');
        this.montageSets.add(montageSet.clone());
    }
    _removeMontageSet(montageSet) {
        if (!montageSet)
            throw new Error('Configuration._removeMontageSet: invalid montageSet');
        this.montageSets.remove(montageSet);
    }
    _removeMontageSets() {
        this.montageSets.removeAll();
    }
    //-----------------------
    // MontBes
    _addMontBes(montBes) {
        if (!montBes)
            throw new Error('Configuration._addMontBes: invalid montBes');
        if (this.getMontBesCount() >= this.getMaxMontBesCount())
            throw new Error('Configuration._addMontBes: max count of MontBes items reached');
        this.montBes.add(montBes.clone());
    }
    _removeMontBes(montBes) {
        if (!montBes)
            throw new Error('Configuration._removeMontBes: invalid montBes');
        this.montBes.remove(montBes);
    }
    _removeAllMontBes() {
        this.montBes.removeAll();
    }
    //-----------------------
    // SmartPlasticsConfiguration
    _removeSmartPlasticsConfiguration() {
        this.model.SmartPlasticsConfiguration = undefined;
    }
    //-----------------------
    // IAArrangement
    /**
     * Sets the IAArragenement to SINGLE. Does not reset existing IAs!
     */
    _removeIAArrangement() {
        this._iaArrangement = echain_shared_igus_fundamentals_1.IaArrangement.single;
    }
    /*************************************************************************************************
     * Getters
     */
    /**
     * Retrieves wether the IAs have positioning information or not
     *
     * @returns If have positioning information or not
     */
    hasIAPositioning() {
        var _a;
        return ((this.ias && this.ias.toArray().some((x) => x.isPlacedInKette())) ||
            (this.leitungen && this.leitungen.toArray().some((x) => x.isPlacedInKette())) ||
            Boolean((_a = this.children) === null || _a === void 0 ? void 0 : _a.some((child) => child.hasIAPositioning())));
    }
    /**
     * Retrieves the amount of leitungen which will be `zugentlastet`.
     *
     * We heavily simplified this implementation since every `Leitung` requires (no matter how) a
     * `Zugentlastung` to be valid. So we are fine when we just return the amount of `Leitung` in the
     * configuration. Please see EKE-2259 for furhter information.
     *
     * @returns The count of leitungen.
     */
    getCountOfLeitungenWithZugentlastung() {
        return {
            count: this.hasAnyZugentlastung() ? this.getLeitungenCount() : 0,
            zesWithoutLeitungen: [],
        };
    }
    /**
     * Retrieves the total weight of the configuration. The total weight includes the weight
     * of the Verpackung and the Fuehrungsrinnen.
     *
     * @param includeFreieLeitung if true weight "freie Leitungen" will be used for calculation
     * @returns The cumulated weight of all articles plus the weight of the verpackung.
     */
    getTotalWeight(includeFreieLeitung = false) {
        let weight = this.getWeightExceptVerpackungRinneAndMontageSet(includeFreieLeitung);
        weight = echain_shared_quantities_1.Weight.kg(weight.kg +
            this.fuehrungsrinnen.getCumulatedWeight().kg +
            this.montageSets.getCumulatedWeight().kg +
            this.verpackungen.getCumulatedWeight().kg);
        return weight;
    }
    /**
     * Retrieves the weigth of this configuration excluding the weight of fuehrungsrinnen
     * @param includeFreieLeitung if true weight "freie Leitungen" will be used for calculation
     * @returns The cululated weight
     */
    getWeightExceptRinneAndMontageSet(includeFreieLeitung = false) {
        let weight = this.getWeightExceptVerpackungRinneAndMontageSet(includeFreieLeitung);
        weight = echain_shared_quantities_1.Weight.kg(weight.kg + this.verpackungen.getCumulatedWeight().kg);
        return weight;
    }
    /**
     * Retrieves the weight of this configuration except the weight of verpackung and fuehrungsrinnen
     * + montage sets.
     *
     * @param includeFreieLeitung if true weight "freie Leitungen" will be used for calculation
     * @returns The cumulated weight of all articles.
     */
    getWeightExceptVerpackungRinneAndMontageSet(includeFreieLeitung = false) {
        // weight of mounting elements + weight of strain reliefs
        let weight = echain_shared_quantities_1.Weight.kg(this.aes.getCumulatedWeight().kg + this.zugentlastungen.getCumulatedWeight().kg);
        // add weight of leitungen
        weight = includeFreieLeitung
            ? echain_shared_quantities_1.Weight.kg(weight.kg + this.leitungen.getCumulatedWeight().kg)
            : echain_shared_quantities_1.Weight.kg(weight.kg + this.leitungen.filterFreieLeitungenOut().getCumulatedWeight().kg);
        const { type, config } = (0, echain_shared_configurations_1.asTypedConfigurationUnion)(this.model);
        switch (type) {
            case 'Empty': // has no kette
            case 'Espool': // not implemented
            case 'EspoolChild': // not implemented
                break;
            case 'Kette': {
                if ((0, echain_shared_articles_1.isRolEchainUnion)(config.Kette)) {
                    if (!(0, echain_shared_articles_1.isCalculateableRolEchain)(config.Kette)) {
                        throw new Error(`${this.className}.getWeightExceptVerpackungRinneAndMontageSet() rolEchain is not calculateable`);
                    }
                    weight = echain_shared_quantities_1.Weight.kg(weight.kg + this.ias.getCumulatedWeightforRolEchain(config.Kette).kg);
                    weight = echain_shared_quantities_1.Weight.kg(weight.kg + echain_shared_articles_oo_1.RolEchainCalculatorFactory.getCalculator(config.Kette).calcTotalWeight(config.Kette).kg);
                    return weight;
                }
                const kette = (0, echain_shared_articles_oo_1.A)(config.Kette);
                weight = echain_shared_quantities_1.Weight.kg(weight.kg + this.ias.getCumulatedWeight(kette).kg);
                const ketteWeight = kette.getMostPreciseWeight();
                weight = echain_shared_quantities_1.Weight.kg(weight.kg + ((ketteWeight === null || ketteWeight === void 0 ? void 0 : ketteWeight.kg) || 0));
                return weight;
            }
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
        return weight;
    }
    /**
     * Retrieves a set of all article ids in this configuration with an existing ArtNr
     *
     * @returns The article ids.
     */
    getAllArticleIds() {
        return echain_shared_articles_oo_1.ArticleUtils.populateAllArticleIds([].concat(this.aes.toArray(), this.fuehrungsrinnen.toArray(), this.ias.toArray(), this.kette ? [this.kette] : [], this.konfektionierungen.toArray(), this.leitungen.toArray(), this.montageSets.toArray(), this.montBes.toArray(), this.verpackungen.toArray(), this.zugentlastungen.toArray()));
    }
    /**
     * Retrieves a set of all article artNrs in this configuration with an existing ArtNr
     *
     * @returns The article artNrs.
     */
    getAllArticleArtNrs() {
        return echain_shared_articles_oo_1.ArticleUtils.populateAllArticleArtNrs([].concat(this.aes.toArray(), this.fuehrungsrinnen.toArray(), this.ias.toArray(), this.kette ? [this.kette] : [], this.konfektionierungen.toArray(), this.leitungen.toArray(), this.montageSets.toArray(), this.montBes.toArray(), this.verpackungen.toArray(), this.zugentlastungen.toArray()));
    }
    /**
     * Retrieves the total weight of all Leitungen or the Zusatzlast
     *
     * @returns The cumulated weight of Leitungen.
     */
    getTotalZusatzlast() {
        if (this.hasAnyLeitung()) {
            return this.leitungen.getTotalWeightPerLength();
        }
        if (this.zusatzlast) {
            return echain_shared_quantities_1.WeightPerLength.kg_km(this.zusatzlast.kg_km);
        }
        return echain_shared_quantities_1.WeightPerLength.kg_km(0);
    }
    hasZusatzlast() {
        return Boolean(this.zusatzlast && this.zusatzlast.kg_km !== 0);
    }
    hasGeschlosseneKette() {
        var _a;
        return (_a = this.kette) === null || _a === void 0 ? void 0 : _a.geschlossen;
    }
    getKetteBauform() {
        var _a;
        return (_a = this.kette) === null || _a === void 0 ? void 0 : _a.bauform;
    }
    getLeitungBeschriftungKonfektionierungTyp() {
        return echain_shared_articles_oo_1.KonfektionierungHelper.getLeitungBeschriftungKonfektionierungTyp(this.leitungen);
    }
    hasAnyVerpackungOfType(verpackungtype) {
        const verpackung = this.verpackungen.toArray().pop();
        return Boolean(verpackung && verpackung.typ === verpackungtype);
    }
    hasAnyFachboden() {
        return this.ias.toArray().some((ia) => ia.isFachboden());
    }
    hasAnyTrennsteg() {
        return this.ias.toArray().some((ia) => ia.isTrennsteg());
    }
    getPaletteOrKartonBreite() {
        const verpackung = this.verpackungen.toArray().pop();
        if (verpackung && verpackung.breite)
            return verpackung.breite;
        return undefined;
    }
    getKetteVerpackungDurchmesser() {
        const verpackung = this.verpackungen.toArray().pop();
        if (verpackung)
            return VerpackungCalculator_1.VerpackungCalculator.calculateKetteDurchmesser(verpackung, this);
        return undefined;
    }
    filterFreieLeitungenOut() {
        return this.leitungen.filterFreieLeitungenOut();
    }
    hasLeitungAnyBeschriftung() {
        return echain_shared_articles_oo_1.KonfektionierungHelper.hasLeitungAnyBeschriftung(this.leitungen);
    }
    /**
     * Retrieves the maximum count of MontBes items that can be added to a configuration
     *
     * @return max allowed count of MontBes items
     */
    getMaxMontBesCount() {
        return this.maxMontBesCount;
    }
    getSmartPlasticsConfiguration() {
        return this.model.SmartPlasticsConfiguration;
    }
    isECPConfigured() {
        var _a;
        return Boolean((_a = this.getSmartPlasticsConfiguration()) === null || _a === void 0 ? void 0 : _a.ECP);
    }
    isECWConfigured() {
        var _a;
        return Boolean((_a = this.getSmartPlasticsConfiguration()) === null || _a === void 0 ? void 0 : _a.ECW);
    }
    generateUUID() {
        const lut = new Array();
        for (let i = 0; i < 256; i++) {
            lut[i] = (i < 16 ? '0' : '') + i.toString(16);
        }
        const d0 = (Math.random() * 0xffffffff) | 0;
        const d1 = (Math.random() * 0xffffffff) | 0;
        const d2 = (Math.random() * 0xffffffff) | 0;
        const d3 = (Math.random() * 0xffffffff) | 0;
        return (lut[d0 & 0xff] +
            lut[(d0 >> 8) & 0xff] +
            lut[(d0 >> 16) & 0xff] +
            lut[(d0 >> 24) & 0xff] +
            '-' +
            lut[d1 & 0xff] +
            lut[(d1 >> 8) & 0xff] +
            '-' +
            lut[((d1 >> 16) & 0x0f) | 0x40] +
            lut[(d1 >> 24) & 0xff] +
            '-' +
            lut[(d2 & 0x3f) | 0x80] +
            lut[(d2 >> 8) & 0xff] +
            '-' +
            lut[(d2 >> 16) & 0xff] +
            lut[(d2 >> 24) & 0xff] +
            lut[d3 & 0xff] +
            lut[(d3 >> 8) & 0xff] +
            lut[(d3 >> 16) & 0xff] +
            lut[(d3 >> 24) & 0xff]);
    }
}
exports.Configuration = Configuration;
//# sourceMappingURL=Configuration.js.map